import React, { forwardRef, useEffect, useState } from "react";
import { Box, Typography, TextField, Grid, Button } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import { IoMdArrowDropdown } from "react-icons/io";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import axios from "axios";
import { toast } from "react-toastify";
import showConfirmDialog from "../../../components/ShowConfirmDialog/ShowConfirmDialog";
import DisplayDate from "../../../components/date/DisplayDate";
import { REACT_DATEPICKER_DISPLAY_FORMAT } from "../../../utils/displayDateFormat";

const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
  <div
    className="custom-date-input"
    onClick={onClick}
    ref={ref}
    style={{
      display: "flex",
      alignItems: "center",
      border: "1px solid #ccc",
      borderRadius: "20px",
      padding: "6px 12px",
      cursor: "pointer",
      gap: "6px",
      width: "fit-content",
      color: "#1c2c4c",
      fontWeight: 500,
      fontSize: "14px",
    }}
  >
    <FaCalendarAlt style={{ fontSize: "14px" }} />
    <span>{value || "Select Date"}</span>
    <IoMdArrowDropdown style={{ fontSize: "16px", marginLeft: "4px" }} />
  </div>
));

const DoctorLeaves = ({ doctorId }) => {
  const [doctorLeaves, setDoctorLeaves] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reason, setReason] = useState("");
  const [leaveToEdit, setLeaveToEdit] = useState(null);
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  // Fetch previous leaves
  const fetchLeaves = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_BACKEND_ROUTE_URL}doctor_leaves/${doctorId}`,
        { headers }
      );
      setDoctorLeaves(response?.data?.data); // Set the response data
    } catch (error) {
      console.error("Error fetching slots data:", error);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleClickEditLeave = async (row) => {
    setLeaveToEdit(row.leave_id);
    setStartDate(new Date(row.leave_from));
    setEndDate(new Date(row.leave_to));
    setReason(row.reason);
  };

  const handleCancelLeave = async (leaveId) => {
    const message = "Are you sure you want to cancel this leave?";
    const confirmed = await showConfirmDialog(message);
    if (confirmed) {
      try {
        const payload = { leave_id: leaveId };
        const response = await axios.delete(
          `${process.env.REACT_APP_BACKEND_ROUTE_URL}doctor_leaves/${doctorId}/`,
          {
            headers,
            data: payload,
          }
        );

        console.log("Cancel Leave Response:", response.data);

        if (response?.data?.status === "Success") {
          toast.success("Leave cancelled successfully!");
          // onSuccess && onSuccess(); // refresh table
          fetchLeaves();
        } else {
          toast.error(
            response?.data?.message ||
              "Failed to cancel leave. Try again later."
          );
        }
      } catch (error) {
        console.error("Error while cancelling leave:", error);
        toast.error("An error occurred while cancelling leave.");
      }
    }
  };

  const columns = [
    { field: "id", headerName: "Sl. No.", width: 80 },
    { field: "doctor_name", headerName: "Doctor Name", width: 200 },
    {
      field: "leave_from",
      headerName: "Leave From",
      width: 200,
      renderCell: (params) => <DisplayDate value={params.value} />,
    },
    {
      field: "leave_to",
      headerName: "Leave To",
      width: 200,
      renderCell: (params) => <DisplayDate value={params.value} />,
    },
    { field: "reason", headerName: "Reason", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleClickEditLeave(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleCancelLeave(params.row.leave_id)}
          >
            Cancel
          </Button>
        </>
      ),
    },
  ];

  const rows = doctorLeaves.map((item, index) => {
    return {
      id: index + 1,
      // facility_id: item.facility_id ?? "-",
      doctor_name: item.doctor_name ?? "-",
      leave_from: item.leave_from ?? "-",
      leave_to: item.leave_to ?? "-",
      reason: item.reason ?? "-",
      leave_id: item.leave_id ?? "-",
    };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate || !endDate || !reason.trim()) {
      toast.error("Please fill in all fields before submitting.");
      return;
    }

    console.log(
      "payload--",
      startDate,
      startDate.toLocaleDateString("en-CA"),
      endDate,
      endDate.toLocaleDateString("en-CA")
    );

    // Format date to yyyy-mm-dd
    const formatDate = (date) => date.toLocaleDateString("en-CA");

    // Build payload dynamically for add or update
    const payload = leaveToEdit
      ? {
          leave_id: leaveToEdit,
          leave_from: formatDate(startDate),
          leave_to: formatDate(endDate),
          reason: reason.trim(),
        }
      : {
          leave_from: formatDate(startDate),
          leave_to: formatDate(endDate),
          reason: reason.trim(),
        };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}doctor_leaves/${doctorId}/`,
        payload,
        { headers }
      );

      console.log("Response:", response.data);

      if (response?.data?.status === "Success") {
        toast.success(
          // response?.data?.message ||
          leaveToEdit
            ? "Leave updated successfully!"
            : "Leave added successfully!"
        );

        // Reset state or trigger parent handlers
        setReason("");
        setStartDate(null);
        setEndDate(null);
        setLeaveToEdit(null);
        fetchLeaves();
        // functionsOnSuccess && functionsOnSuccess();
        // onClose && onClose();
      } else {
        toast.error(
          response?.data?.message ||
            "Some error occurred. Please try again later."
        );
      }
    } catch (error) {
      console.error("Error during leave submission:", error);
      toast.error("An error occurred while submitting leave.");
    }
  };

  const getRowClassName = (params) => {
    return "custom-row";
  };

  const handleCancelEdit = () => {
    setReason("");
    setStartDate(null);
    setEndDate(null);
    setLeaveToEdit(null);
  };

  return (
    <Box
      sx={{
        p: 3,

        // maxWidth: 800,
        mx: "auto",
      }}
    >
      {/* Start and End Dates in the same line */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
            <Typography
              fontWeight={500}
              fontSize="14px"
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minWidth: 68,
                color: "#0e204d",
              }}
            >
              Start Date:
            </Typography>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                // Only update endDate if it's not set, or if it's before the new start date
                if (!endDate || date > endDate) {
                  setEndDate(date);
                }
              }}
              dateFormat={REACT_DATEPICKER_DISPLAY_FORMAT}
              selectsStart
              customInput={<CustomDateInput />}
              popperPlacement="bottom-start"
              popperProps={{
                strategy: "fixed",
                modifiers: [
                  {
                    name: "zIndex",
                    enabled: true,
                    phase: "write",
                    fn({ state }) {
                      state.styles.popper.zIndex = 99999;
                    },
                  },
                ],
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography
              fontWeight={500}
              fontSize="14px"
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minWidth: 68,
                color: "#0e204d",
              }}
            >
              End Date:
            </Typography>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat={REACT_DATEPICKER_DISPLAY_FORMAT}
              selectsEnd
              minDate={startDate}
              customInput={<CustomDateInput />}
              popperPlacement="bottom-start"
              popperProps={{
                strategy: "fixed",
                modifiers: [
                  {
                    name: "zIndex",
                    enabled: true,
                    phase: "write",
                    fn({ state }) {
                      state.styles.popper.zIndex = 99999;
                    },
                  },
                ],
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Reason Field */}
      <Box mt={2}>
        <Typography
          fontWeight={500}
          fontSize="14px"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minWidth: 68,
            color: "#0e204d",
          }}
        >
          Reason:
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder="Enter reason for leave"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 255 }}
        />
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            color: "text.secondary",
            mt: 0.5,
            fontSize: "0.75rem",
          }}
        >
          {reason.length}/255
        </Typography>
      </Box>

      {/* Submit Button */}

      <Box mt={1} mb={1} textAlign="right">
        {leaveToEdit && (
          <Button
            variant="contained"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#121959",
              color: "#fff",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              "&:hover": {
                backgroundColor: "#121959",
              },
              mr: 1,
            }}
            onClick={handleCancelEdit}
            size="small"
          >
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          sx={{
            borderRadius: "10px",
            backgroundColor: "#121959",
            color: "#fff",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 1 },
            "&:hover": {
              backgroundColor: "#121959",
            },
          }}
          onClick={handleSubmit}
          size="small"
        >
          {leaveToEdit ? "Update" : "Add"}
        </Button>
      </Box>

      {doctorLeaves.length > 0 && (
        <DataGrid
          rows={rows}
          columns={columns}
          components={{
            Toolbar: () => (
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  mb: 2,
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={8}
                  sx={{ display: "flex", gap: 1 }}
                >
                  <GridToolbarColumnsButton />
                  <GridToolbarFilterButton />
                  <GridToolbarDensitySelector />
                </Grid>
              </Grid>
            ),
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[5, 10]}
          getRowClassName={getRowClassName}
          sx={{
            "& .MuiDataGrid-root": {
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              fontFamily: "Arial, sans-serif",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#125386",
              color: "white !important",
              fontSize: "13px",
              fontWeight: "600",
              minHeight: "48px !important",
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                fontWeight: "600",
                fontSize: "13px",
              },
              "& .MuiDataGrid-columnHeader": {
                borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                padding: "0 12px",
              },
              "& .MuiDataGrid-columnHeader:last-child": {
                borderRight: "none",
              },
              "& .MuiDataGrid-iconButtonContainer": {
                color: "white",
              },
              "& .MuiDataGrid-menuIcon": {
                color: "white",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "white",
              },
              "& .MuiDataGrid-columnHeaderSortable": {
                "& .MuiDataGrid-sortIcon": {
                  color: "white",
                },
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                "& .MuiDataGrid-sortIcon": {
                  color: "white",
                },
              },
              "& .MuiSvgIcon-root": {
                color: "white",
              },
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "white",
              minHeight: "40px !important",
              "&:nth-of-type(even)": {
                backgroundColor: "white",
              },
              "&:hover": {
                backgroundColor: "#f8f9fa",
              },
              "&.Mui-selected": {
                backgroundColor: "#e3f2fd",
                "&:hover": {
                  backgroundColor: "#e3f2fd",
                },
              },
              borderBottom: "1px solid #e0e0e0",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid #e0e0e0",
              fontSize: "12px",
              color: "#374151",
              padding: "8px 12px",
              minHeight: "40px !important",
              "&:last-child": {
                borderRight: "none",
              },
              "&:focus": {
                outline: "none",
              },
            },
            "& .MuiDataGrid-cellContent": {
              fontSize: "12px",
              color: "#374151",
              fontWeight: "400",
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "#f8f9fa",
              borderTop: "1px solid #e0e0e0",
              minHeight: "52px",
            },
            "& .MuiTablePagination-root": {
              color: "#374151",
              fontSize: "12px",
            },
            "& .MuiDataGrid-toolbarContainer": {
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e0e0e0",
              padding: "8px 16px",
            },
          }}
        />
        // <DataGrid
        //   rows={rows}
        //   columns={columns}
        //   components={{
        //     Toolbar: () => (
        //       <Grid
        //         container
        //         spacing={2}
        //         alignItems="center"
        //         justifyContent="space-between"
        //         sx={{ mb: 2 }}
        //       >
        //         <Grid
        //           item
        //           xs={12}
        //           sm={6}
        //           md={8}
        //           sx={{ display: "flex", gap: 1 }}
        //         >
        //           <GridToolbarColumnsButton />
        //           <GridToolbarFilterButton />
        //           <GridToolbarDensitySelector />
        //         </Grid>
        //       </Grid>
        //     ),
        //   }}
        //   initialState={{
        //     pagination: {
        //       paginationModel: { page: 0, pageSize: 10 },
        //     },
        //   }}
        //   pageSizeOptions={[5, 10]}
        //   getRowClassName={getRowClassName}
        //   sx={{
        //     "& .MuiDataGrid-columnHeaders": {
        //       backgroundColor: "#125386",
        //       color: "white !important", // <-- Force white text for headers
        //       fontSize: "13px",
        //       fontWeight: "600",
        //       minHeight: "48px !important",
        //     },
        //     "& .MuiDataGrid-columnHeaderTitle": {
        //       color: "white !important", // <-- Add important to override defaults
        //       fontWeight: "600",
        //       fontSize: "13px",
        //     },
        //     "& .MuiDataGrid-columnHeader, & .MuiSvgIcon-root, & .MuiDataGrid-iconButtonContainer":
        //       {
        //         color: "white !important",
        //       },
        //     "& .MuiDataGrid-sortIcon": {
        //       color: "white !important",
        //     },
        //     "& .MuiDataGrid-menuIcon": {
        //       color: "white !important",
        //     },
        //     "& .MuiDataGrid-columnHeaderTitleContainer": {
        //       "& .MuiDataGrid-sortIcon": {
        //         color: "white !important",
        //       },
        //     },
        //     "& .MuiDataGrid-root": {
        //       border: "1px solid #e0e0e0",
        //       borderRadius: "4px",
        //       fontFamily: "Arial, sans-serif",
        //     },
        //     "& .MuiDataGrid-row": {
        //       backgroundColor: "white",
        //       "&:hover": { backgroundColor: "#f8f9fa" },
        //       "&.Mui-selected": {
        //         backgroundColor: "#e3f2fd",
        //         "&:hover": { backgroundColor: "#e3f2fd" },
        //       },
        //       borderBottom: "1px solid #e0e0e0",
        //     },
        //     "& .MuiDataGrid-cell": {
        //       borderRight: "1px solid #e0e0e0",
        //       fontSize: "12px",
        //       color: "#374151",
        //       padding: "8px 12px",
        //       "&:last-child": { borderRight: "none" },
        //       "&:focus": { outline: "none" },
        //     },
        //     "& .MuiDataGrid-footerContainer": {
        //       backgroundColor: "#f8f9fa",
        //       borderTop: "1px solid #e0e0e0",
        //       minHeight: "52px",
        //     },
        //     "& .MuiTablePagination-root": {
        //       color: "#374151",
        //       fontSize: "12px",
        //     },
        //   }}
        // />
      )}
    </Box>
  );
};

export default DoctorLeaves;
